import React, { useEffect, useState } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth, hasMinRole } from "@/contexts/AuthContext";
import AuditTrailDrawer from "@/components/app/AuditTrailDrawer";
import { Loading, Empty } from "./_shared";
import StaffFullProfile from "./StaffFullProfile";

export default function PastEmployees() {
  const { user } = useAuth();
  const isAdmin = hasMinRole(user, "admin");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [reason, setReason] = useState("all");
  const [selected, setSelected] = useState(null);
  const [busyBackfill, setBusyBackfill] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hr/staff/past");
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runBackfill = async () => {
    setBusyBackfill(true);
    try {
      const { data } = await api.post("/hr/separations/backfill-archive");
      const moved = (data?.moved || []).length;
      if (moved > 0) {
        toast.success(`Moved ${moved} employee(s) to Past Employees.`);
        await load();
      } else {
        toast.info("Everyone already up to date — no closed separations needed archiving.");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyBackfill(false); }
  };

  const reasons = Array.from(new Set(items
    .map((s) => (s.archived_reason || s.separation?.reason || s.separation?.separation_type))
    .filter(Boolean)));

  const filtered = items.filter((s) => {
    if (reason !== "all") {
      const r = (s.archived_reason || s.separation?.reason || s.separation?.separation_type || "");
      if (r.toLowerCase() !== reason.toLowerCase()) return false;
    }
    if (search) {
      const blob = `${s.full_name} ${s.employee_code} ${s.email} ${s.department} ${s.designation}`.toLowerCase();
      if (!blob.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  return (
    <div className="space-y-4" data-testid="past-employees-panel">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="font-display text-base font-semibold text-slate-900">Past Employees</h3>
          <p className="text-xs text-slate-500">
            Former staff whose separation has been finalised. Click any name to open the complete historical profile.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={runBackfill} disabled={busyBackfill}
                    data-testid="past-backfill-btn"
                    title="Move any closed separations whose employee was not yet archived into Past Employees">
              {busyBackfill ? "Scanning…" : "Sync from closed separations"}
            </Button>
          )}
          <Input
            placeholder="Search name, code, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full sm:w-64"
            data-testid="past-search"
          />
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger className="w-full sm:w-52" data-testid="past-reason-filter">
              <SelectValue placeholder="All reasons" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All reasons</SelectItem>
              {reasons.map((r) => (
                <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="dc-card overflow-hidden">
        {loading ? <Loading />
         : filtered.length === 0 ? (
           <Empty text={items.length === 0
             ? "No past employees yet. Records will appear here automatically once a separation is finalised."
             : "No past employees match the current filters."} />
         ) : (
          <>
            <table className="hidden w-full md:table">
              <thead className="bg-slate-50 text-left text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-5 py-3">Name</th>
                  <th className="px-5 py-3">Code</th>
                  <th className="px-5 py-3">Department · Role</th>
                  <th className="px-5 py-3">Reason</th>
                  <th className="px-5 py-3">Effective</th>
                  <th className="px-5 py-3">Closed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((s) => (
                  <tr
                    key={s.id}
                    className="text-sm cursor-pointer hover:bg-slate-50 transition"
                    onClick={() => setSelected(s)}
                    data-testid={`past-row-${s.id}`}
                  >
                    <td className="px-5 py-3 font-semibold text-slate-900">{s.full_name}</td>
                    <td className="px-5 py-3 font-mono-alt text-xs">{s.employee_code || "—"}</td>
                    <td className="px-5 py-3 text-xs text-slate-600">
                      {s.department || "—"} · {s.designation || "—"}
                    </td>
                    <td className="px-5 py-3">
                      <span className="rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-mono-alt uppercase tracking-wider text-amber-800">
                        {(s.archived_reason || s.separation?.reason || s.separation?.separation_type || "separated").replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-mono-alt text-xs">{formatDate(s.separation?.effective_date || s.end_date || s.archived_at)}</td>
                    <td className="px-5 py-3 font-mono-alt text-xs">{s.archived_at ? formatDate(s.archived_at) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <ul className="divide-y divide-slate-100 md:hidden">
              {filtered.map((s) => (
                <li
                  key={s.id}
                  className="p-4 cursor-pointer hover:bg-slate-50 active:bg-slate-100 transition"
                  onClick={() => setSelected(s)}
                  data-testid={`past-mrow-${s.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900">{s.full_name}</div>
                      <div className="text-[11px] text-slate-500">{s.employee_code || "—"} · {s.department || "—"}</div>
                      <div className="text-[11px] text-slate-500">{s.designation || "—"}</div>
                    </div>
                    <span className="shrink-0 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-mono-alt uppercase tracking-wider text-amber-800">
                      {(s.archived_reason || s.separation?.reason || s.separation?.separation_type || "separated").replace(/_/g, " ")}
                    </span>
                  </div>
                  <div className="mt-2 text-[11px] text-slate-500">
                    Closed {s.archived_at ? formatDate(s.archived_at) : "—"} · Effective {formatDate(s.separation?.effective_date || s.end_date || s.archived_at)}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogTitle className="sr-only">Past employee · {selected?.full_name || ""}</DialogTitle>
          <DialogDescription className="sr-only">Historical profile of past employee {selected?.full_name || ""} including separation reason, off-boarding details, and full employment history.</DialogDescription>
          {selected && (
            <>
              {selected.separation && (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50/70 p-3">
                  <div className="text-[10px] font-mono-alt uppercase tracking-widest text-amber-800">Separation summary</div>
                  <div className="mt-1 grid grid-cols-2 gap-2 text-xs text-amber-900 sm:grid-cols-4">
                    <div><span className="text-amber-700/70">Reason:</span> {(selected.separation.reason || selected.separation.separation_type || "—").replace(/_/g, " ")}</div>
                    <div><span className="text-amber-700/70">Effective:</span> {formatDate(selected.separation.effective_date)}</div>
                    <div><span className="text-amber-700/70">Initiated by:</span> {selected.separation.created_by || "—"}</div>
                    <div><span className="text-amber-700/70">Closed by:</span> {selected.separation.closed_by || "—"}</div>
                    <div className="col-span-2 sm:col-span-4"><span className="text-amber-700/70">Off-boarding completed:</span> {selected.archived_at ? formatDate(selected.archived_at) : "—"}</div>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <AuditTrailDrawer entity="hr_separation" entityId={selected.separation.id} label="Separation audit" />
                    <AuditTrailDrawer entity="hr_staff" entityId={selected.id} label="Employee audit" />
                  </div>
                </div>
              )}
              <StaffFullProfile staffId={selected.id} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
